/**
 * Cookies de anúncio (Meta e Google) que acompanham a inscrição, para o servidor avisar a compra
 * quando o pagamento for aprovado. Só com o "Aceitar" do aviso de cookies.
 */

export type AdTrackingPayload = {
  consent: true;
  fbp?: string;
  fbc?: string;
  gaClientId?: string;
  gaSessionId?: string;
  sourceUrl?: string;
};

export const parseCookies = (cookieHeader: string): Record<string, string> =>
  Object.fromEntries(
    cookieHeader
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const index = part.indexOf('=');
        const name = index < 0 ? part : part.slice(0, index);
        const value = index < 0 ? '' : part.slice(index + 1);
        try {
          return [name, decodeURIComponent(value)];
        } catch {
          return [name, value];
        }
      })
  );

/** _ga = "GA1.1.123456789.1730000000" → client_id "123456789.1730000000" */
export const gaClientIdFrom = (gaCookie?: string) => {
  const match = gaCookie?.match(/^GA\d\.\d\.(\d+\.\d+)$/);
  return match?.[1];
};

/**
 * _ga_<ID> tem a sessão atual. Formato antigo "GS1.1.1730000000.3.1..." e novo "GS2.1.s1730000000$o3$g1...".
 */
export const gaSessionIdFrom = (sessionCookie?: string) => {
  const match =
    sessionCookie?.match(/^GS1\.\d\.(\d+)\./) ?? sessionCookie?.match(/^GS2\.\d\.s(\d+)/);
  return match?.[1];
};

/** Sem _fbc ainda (o pixel grava depois), monta pelo fbclid do link do anúncio, como a Meta indica. */
export const fbcFrom = (fbcCookie: string | undefined, search: string, now: number) => {
  if (fbcCookie) {
    return fbcCookie;
  }
  const fbclid = new URLSearchParams(search).get('fbclid');
  return fbclid ? `fb.1.${now}.${fbclid}` : undefined;
};

export const buildAdTracking = (input: {
  consent: boolean;
  cookieHeader: string;
  search: string;
  href: string;
  ga4MeasurementId?: string;
  now: number;
}): AdTrackingPayload | undefined => {
  if (!input.consent) {
    return undefined;
  }
  const cookies = parseCookies(input.cookieHeader);
  const sessionCookie = input.ga4MeasurementId
    ? cookies[`_ga_${input.ga4MeasurementId.replace(/^G-/, '')}`]
    : undefined;
  const payload: AdTrackingPayload = {
    consent: true,
    fbp: cookies._fbp,
    fbc: fbcFrom(cookies._fbc, input.search, input.now),
    gaClientId: gaClientIdFrom(cookies._ga),
    gaSessionId: gaSessionIdFrom(sessionCookie),
    sourceUrl: input.href.split('?')[0],
  };
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  ) as AdTrackingPayload;
};

/** Marco do organizador que o servidor confirmou como primeira vez (vem na resposta da API). */
export type AdConversion = {
  milestone: 'registration' | 'first-event' | 'first-publication';
  /** Mesmo id que o servidor manda à Meta: ela descarta a cópia */
  eventId: string;
};

const META_CONVERSION: Record<AdConversion['milestone'], { method: 'track' | 'trackCustom'; name: string }> = {
  registration: { method: 'track', name: 'CompleteRegistration' },
  'first-event': { method: 'track', name: 'StartTrial' },
  'first-publication': { method: 'trackCustom', name: 'PublicouEvento' },
};

const GA4_CONVERSION: Record<AdConversion['milestone'], string> = {
  registration: 'sign_up',
  'first-event': 'organizer_first_event',
  'first-publication': 'organizer_first_publication',
};

/** Chamadas do pixel e do gtag para o marco (os nomes batem com os do servidor). */
export const adConversionCalls = (conversion: AdConversion) => {
  const meta = META_CONVERSION[conversion.milestone];
  return {
    fbq: [meta.method, meta.name, { currency: 'BRL', value: 0 }, { eventID: conversion.eventId }] as const,
    gtag: ['event', GA4_CONVERSION[conversion.milestone]] as const,
  };
};
